import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HPButton from "@components/common/Button";

type PinPosition = "none" | "start" | "end";

interface SliceRowProps {
  name: string;
  displayName: string;
  isOverridden: boolean;
  isRollup: boolean;
  pinPosition: PinPosition;
  isCustomOrder: boolean;
  showPinControl: boolean;
  showColorSwatch: boolean;
  sliceColor: string;
  isEditing: boolean;
  editValue: string;
  onPinChange: (position: PinPosition) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditValueChange: (value: string) => void;
  onResetLabel: () => void;
}

const SliceRow: React.FC<SliceRowProps> = ({
  name,
  displayName,
  isOverridden,
  isRollup,
  pinPosition,
  isCustomOrder,
  showPinControl,
  showColorSwatch,
  sliceColor,
  isEditing,
  editValue,
  onPinChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditValueChange,
  onResetLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name, disabled: !isCustomOrder });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: "white",
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag handle — only in custom order mode */}
      {isCustomOrder && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Pin control — only in non-custom sort modes */}
      {showPinControl && (
        <Select
          value={pinPosition}
          onChange={(e) => onPinChange(e.target.value as PinPosition)}
          size="small"
          variant="standard"
          disableUnderline
          sx={{
            fontSize: 11,
            flexShrink: 0,
            width: 84,
            "& .MuiSelect-select": { py: 0, px: 0.5 },
          }}
        >
          <MenuItem value="none" sx={{ fontSize: 12 }}>
            No pin
          </MenuItem>
          <MenuItem value="start" sx={{ fontSize: 12 }}>
            ↑ Start
          </MenuItem>
          <MenuItem value="end" sx={{ fontSize: 12 }}>
            ↓ End
          </MenuItem>
        </Select>
      )}

      {/* Label or edit field */}
      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: isOverridden ? 600 : 400,
              }}
            >
              {displayName}
            </Typography>
            {isOverridden && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                Original: {name}
              </Typography>
            )}
          </Box>
          {isRollup && (
            <Chip label="Rollup" size="small" sx={{ fontSize: 10, height: 18 }} />
          )}
        </Box>
      )}

      {/* Action buttons */}
      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          {/* Rollups are named/renamed in the Rollups section, not here */}
          {!isRollup && (
            <IconButton size="small" onClick={onStartEdit} sx={{ flexShrink: 0 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}

          {/* Static color swatch — visible only when Custom (per-slice) palette is active */}
          {showColorSwatch && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: sliceColor,
                flexShrink: 0,
                border: "1px solid rgba(0,0,0,0.12)",
              }}
            />
          )}

          {isOverridden && (
            <HPButton size="small" variant="text" onClick={onResetLabel}>
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

export default SliceRow;
